/**
 * Universal Settings (Settings v2, owner decision D13) — composed from the
 * SHARED sections only (@levelup/ui-management ≥ 1.20, central-billing ≥ 1.5,
 * central-context ≥ 1.8). This file holds no Settings rules of its own: it
 * wires the shared sections to the app's Central transport (`fetcher`) and to
 * the app's existing refresh / terminal-auth paths.
 *
 *   app Settings → Shared UI → app transport (BFF or browser-direct) → Central
 *
 * The 12 universal tabs, in order: Profile · Workspace · Organization · Members
 * · My Work Availability · Availability · Outcomes · Billing · Credits ·
 * Recommendation · Apps & Access · Notifications. App tabs follow (order ≥ 600).
 *
 * Hidden when not applicable (D13): Recommendation / Availability when Central
 * answers 404 (feature off); a tab the user cannot read (403); tabs the app
 * declares not applicable. Read-only users get "View only" tabs; every control
 * is still gated by Central's permissions (Central enforces regardless).
 *
 * The app may supply its own content for a universal tab (`overrides`) — e.g.
 * its existing billing composition — but cannot replace the tab itself.
 */
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import {
  SettingsShell,
  ProfileSection,
  AccountSecuritySection,
  WorkspaceSection,
  NotificationPreferencesSection,
  buildCentralPasswordResetUrl,
  canonicalSettingsTabId,
  type SettingsTabConfig,
  type ProfileDraft,
  type WorkspaceDraft,
  type WorkspacePurpose,
} from '@levelup/ui-management/settings';
import {
  MembershipSection,
  MemberAccessPanel,
  type InvitePayload,
} from '@levelup/ui-management/membership';
import {
  CentralAppCatalog,
  CreditPackPicker,
  CreditTransactionsSection,
  CreditsUsageSection,
  MemberAppAccessSummary,
  MyAvailabilitySection,
  OrganizationManager,
  OrganizationSection,
  OutcomesSection,
  RecommendationSection,
  RolesPermissionsSection,
  TeamsSection,
  WorkspaceAvailabilitySection,
  useCentralResource,
  type CentralCreditsPayload,
} from '@levelup/ui-management/central';
import {
  createCentralAssetsClient,
  createCentralAvailabilityClient,
  createCentralBillingClient,
  createCentralOrganizationClient,
  createCentralSettingsClient,
  createCentralTeamsClient,
  uploadCentralImageAsset,
  unwrapCentralResult,
  classifyAvailabilityProbe,
  type CentralFetcher,
  type CentralResult,
} from '@levelup/central-billing';
import { buildMembershipPermissions } from '@levelup/central-context';
import { BillingPanel } from './UniversalBilling';

// ─────────────────────────────────────────────────────────────────────────────
// Inputs
// ─────────────────────────────────────────────────────────────────────────────

export type WorkspacePermissionFlags = Partial<
  Record<'manageMembers' | 'manageBilling' | 'transferOwnership' | 'closeWorkspace', boolean>
>;

export interface UniversalSettingsWorkspace {
  id: string;
  name: string;
  slug?: string | null;
  role: string;
  /** switch-context `type` (personal | team | enterprise …) — plan filtering. */
  type?: string | null;
  /** Central `purpose` (personal | business | client) — never guessed. */
  purpose?: string | null;
  avatarUrl?: string | null;
  status?: string | null;
  createdAt?: string | null;
  onboardingVersion?: string | null;
  permissions?: WorkspacePermissionFlags | null;
}

export interface UniversalSettingsUser {
  id: string;
  email: string;
  name?: string | null;
  avatarUrl?: string | null;
}

export interface UniversalAppTab extends SettingsTabConfig {
  render: () => ReactNode;
}

export interface UniversalSettingsProps {
  /** The app's Central transport (`proxyFetcher` for BFF apps, else `browserDirectFetcher`). */
  fetcher: CentralFetcher;
  centralUrl: string;
  /** This app's Central app id (invite routing, catalog highlighting). */
  appId: string;
  workspace: UniversalSettingsWorkspace;
  user: UniversalSettingsUser;
  /** switch-context apps, for app display names. */
  apps?: ReadonlyArray<{ appId: string; displayName?: string | null; name?: string | null }>;
  /** Canonical or legacy tab id (aliases resolve: billing-credits → billing, general → workspace, roles → members). */
  activeTab: string;
  /** Sub-view (`plans`, `invoices`, `teams`, `roles`, …). */
  view?: string | null;
  /** `?outcome=<code>` from the page's URL: pre-ticked in Billing's outcomes-first picker (a prop, never read inside). */
  outcome?: string | null;
  /** Go to a tab / sub-view; `extra.outcome` becomes `&outcome=` (any other navigation drops it). */
  onNavigate: (tab: string, view?: string | null, extra?: { outcome?: string | null }) => void;
  /** Absolute app URL Central should return to for this tab (billing, packs, hand-offs). */
  returnTo: (tab: string, view?: string | null) => string;
  /** Central answered 401 — the app's terminal-auth path (sign in again). */
  onUnauthenticated: () => void;
  /** After a change Central accepted — the app's existing boot / switch-context refresh. */
  onChanged: () => void;
  onSignOut?: () => void;
  /** Switch workspace from the organization tree (selectable nodes only). */
  onSelectWorkspace?: (workspaceId: string) => void;
  /** Open an app from Apps & Access (default: Central's launch URL). */
  onOpenApp?: (launchUrl: string) => void;
  /** App content for a universal tab (the tab stays; only its content is the app's). */
  overrides?: Partial<Record<string, () => ReactNode>>;
  /** App-specific tabs (order ≥ 600). */
  appTabs?: readonly UniversalAppTab[];
  /** Universal tabs that genuinely do not apply to this app. */
  notApplicable?: readonly string[];
  /** In-app password change (D3). Default true. */
  allowPasswordChange?: boolean;
  /** The user may edit their own name / photo here. Default true (Central allows self-edit). */
  canEditProfile?: boolean;
  title?: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

const MANAGER_ROLES = new Set(['owner', 'admin']);

function roleOf(ws: UniversalSettingsWorkspace): string {
  return String(ws.role || '').toLowerCase();
}

function permission(ws: UniversalSettingsWorkspace, key: keyof WorkspacePermissionFlags): boolean {
  const explicit = ws.permissions?.[key];
  if (typeof explicit === 'boolean') return explicit;
  // Central's own role rules (capabilitiesService.resolveWorkspacePermissions).
  const role = roleOf(ws);
  if (key === 'manageMembers' || key === 'manageBilling') return MANAGER_ROLES.has(role);
  return role === 'owner';
}

function purposeOf(ws: UniversalSettingsWorkspace): WorkspacePurpose | null {
  const p = ws.purpose;
  return p === 'personal' || p === 'business' || p === 'client' ? p : null;
}

/** Central member row → the shape the shared membership UI renders. */
function toWorkspaceMember(m: Record<string, unknown>) {
  return {
    userId: String(m.userId ?? ''),
    email: String(m.email ?? ''),
    fullName: (typeof m.fullName === 'string' ? m.fullName : typeof m.name === 'string' ? m.name : null) as string | null,
    avatar: (m.avatar ?? (typeof m.avatarUrl === 'string' ? { url: m.avatarUrl } : null)) as never,
    role: String(m.role ?? 'member') as 'owner' | 'admin' | 'member' | 'viewer',
    roleLabel: (typeof m.roleLabel === 'string' ? m.roleLabel : null) as string | null,
    joinedAt: String(m.joinedAt ?? ''),
    status: (m.status === 'suspended' ? 'suspended' : 'active') as 'active' | 'suspended',
    ...(m.appRoles && typeof m.appRoles === 'object' ? { appRoles: m.appRoles as Record<string, string> } : {}),
  };
}

function toPendingInvite(i: Record<string, unknown>) {
  return {
    inviteId: String(i.inviteId ?? i.id ?? ''),
    email: String(i.email ?? ''),
    role: String(i.role ?? 'member') as 'owner' | 'admin' | 'member' | 'viewer',
    roleLabel: (typeof i.roleLabel === 'string' ? i.roleLabel : null) as string | null,
    invitedBy: (i.invitedBy ?? null) as never,
    sentAt: String(i.sentAt ?? i.createdAt ?? ''),
    expiresAt: (typeof i.expiresAt === 'string' ? i.expiresAt : null) as string | null,
  };
}

/** The latest value, readable from callbacks/effects (updated after each render, never during it). */
function useLatest<T>(value: T) {
  const ref = useRef(value);
  useEffect(() => {
    ref.current = value;
  });
  return ref;
}

// ─────────────────────────────────────────────────────────────────────────────
// Page
// ─────────────────────────────────────────────────────────────────────────────

export function UniversalSettings(props: UniversalSettingsProps) {
  const { fetcher, workspace, activeTab, onNavigate, notApplicable, appTabs, overrides } = props;
  const clients = useMemo(
    () => ({
      billing: createCentralBillingClient(fetcher),
      settings: createCentralSettingsClient(fetcher),
      assets: createCentralAssetsClient(fetcher),
      availability: createCentralAvailabilityClient(fetcher),
      organization: createCentralOrganizationClient(fetcher),
      teams: createCentralTeamsClient(fetcher, { workspaceId: workspace.id }),
    }),
    [fetcher, workspace.id],
  );

  const isManager = MANAGER_ROLES.has(roleOf(workspace));
  const unauth = useLatest(props.onUnauthenticated);
  const handlers = useMemo(() => ({ onUnauthenticated: () => unauth.current() }), [unauth]);

  // ── Tabs that do not apply (D13) ──
  // Kept per workspace, so a switch starts from "nothing hidden" without a
  // synchronous reset inside the probe effect.
  const [probed, setProbed] = useState<{ workspaceId: string; hidden: ReadonlySet<string> }>(() => ({
    workspaceId: workspace.id,
    hidden: new Set(),
  }));
  const probedHidden = useMemo(
    () => (probed.workspaceId === workspace.id ? probed.hidden : new Set<string>()),
    [probed, workspace.id],
  );
  const hide = useCallback(
    (tab: string) => {
      setProbed((prev) => {
        const base = prev.workspaceId === workspace.id ? prev.hidden : new Set<string>();
        if (base.has(tab)) return prev;
        return { workspaceId: workspace.id, hidden: new Set(base).add(tab) };
      });
    },
    [workspace.id],
  );
  useEffect(() => {
    let cancelled = false;
    // Availability: 404 → the feature is off for the workspace (both tabs).
    void clients.availability.getCurrentSession().then((r: CentralResult<unknown>) => {
      if (cancelled) return;
      const verdict = classifyAvailabilityProbe(r.ok ? 200 : r.status);
      if (verdict === 'unauthenticated') unauth.current();
      if (verdict === 'disabled') {
        hide('my-availability');
        hide('availability');
      }
    });
    // Recommendation: 404 → capacity mode is off.
    void clients.billing.getCapacityRecommendation(workspace.id).then((r: CentralResult<unknown>) => {
      if (cancelled) return;
      if (!r.ok && (r.status === 404 || r.status === 403)) hide('recommendation');
    });
    return () => {
      cancelled = true;
    };
  }, [clients, workspace.id, hide, unauth]);

  const hiddenTabs = useMemo(() => {
    const h = new Set<string>([...(notApplicable ?? []), ...Array.from(probedHidden)]);
    // The workspace-wide availability admin is owner/admin (members: no read access).
    if (!isManager) h.add('availability');
    return Array.from(h);
  }, [notApplicable, probedHidden, isManager]);

  const readOnlyTabs = useMemo(
    () => (isManager ? [] : ['workspace', 'organization', 'billing', 'credits', 'outcomes']),
    [isManager],
  );

  const tab = canonicalSettingsTabId(activeTab) ?? activeTab;
  const appTab = appTabs?.find((t) => t.id === activeTab);
  const extraTabs = useMemo(
    () =>
      (appTabs ?? []).map((t) => {
        const cfg: Partial<UniversalAppTab> = { ...t };
        delete cfg.render;
        return cfg as SettingsTabConfig;
      }),
    [appTabs],
  );

  const content = (() => {
    if (appTab) return appTab.render();
    const override = overrides?.[tab];
    if (override) return override();
    switch (tab) {
      case 'profile':
        return <ProfileTab {...props} clients={clients} />;
      case 'workspace':
        return <WorkspaceTab {...props} clients={clients} />;
      case 'organization':
        return <OrganizationTab {...props} clients={clients} handlers={handlers} />;
      case 'members':
        return <MembersTab {...props} clients={clients} handlers={handlers} />;
      case 'my-availability':
        return (
          <MyAvailabilityTab {...props} clients={clients} handlers={handlers} onUnavailable={() => hide('my-availability')} />
        );
      case 'availability':
        return <AvailabilityTab {...props} clients={clients} handlers={handlers} onUnavailable={() => hide('availability')} />;
      case 'outcomes':
        return (
          <OutcomesSection
            client={clients.billing}
            workspaceId={workspace.id}
            workspaceType={purposeOf(workspace) ?? (workspace.type === 'personal' ? 'personal' : 'business')}
            canManage={permission(workspace, 'manageBilling')}
            onReviewPlans={(_requirement, picked) => onNavigate('billing', 'plans', { outcome: picked.code })}
            onChanged={props.onChanged}
            {...handlers}
            onUnavailable={() => hide('outcomes')}
          />
        );
      case 'billing':
        return <BillingTab {...props} clients={clients} handlers={handlers} />;
      case 'credits':
        return <CreditsTab {...props} clients={clients} handlers={handlers} />;
      case 'recommendation':
        return (
          <RecommendationSection
            client={clients.billing}
            workspaceId={workspace.id}
            onComparePlans={() => onNavigate('billing', 'plans')}
            {...handlers}
            onUnavailable={() => hide('recommendation')}
          />
        );
      case 'apps-access':
        return <AppsAccessTab {...props} clients={clients} handlers={handlers} />;
      case 'notifications':
        return <NotificationsTab {...props} clients={clients} handlers={handlers} />;
      default:
        return null;
    }
  })();

  return (
    <SettingsShell
      layout="universal"
      activeTab={activeTab}
      onTabChange={(next) => onNavigate(next, null)}
      hiddenTabs={hiddenTabs}
      readOnlyTabs={readOnlyTabs}
      extraTabs={extraTabs}
      title={props.title ?? 'Settings'}
    >
      <div data-testid={`universal-settings-${appTab ? activeTab : tab}`}>{content}</div>
    </SettingsShell>
  );
}

type Clients = {
  billing: ReturnType<typeof createCentralBillingClient>;
  settings: ReturnType<typeof createCentralSettingsClient>;
  assets: ReturnType<typeof createCentralAssetsClient>;
  availability: ReturnType<typeof createCentralAvailabilityClient>;
  organization: ReturnType<typeof createCentralOrganizationClient>;
  teams: ReturnType<typeof createCentralTeamsClient>;
};
type Handlers = { onUnauthenticated: () => void };
type TabProps = UniversalSettingsProps & { clients: Clients; handlers: Handlers };

/** Settle a Central mutation: 401 → terminal-auth path (never a form message); refusal → thrown. */
async function settle<T>(result: CentralResult<T>, onUnauthenticated: () => void): Promise<T> {
  if (!result.ok && result.status === 401) {
    onUnauthenticated();
    return new Promise<T>(() => undefined);
  }
  return unwrapCentralResult(result);
}

// ── Profile + account security ──────────────────────────────────────────────

function ProfileTab({ user, clients, centralUrl, onUnauthenticated, onChanged, onSignOut, allowPasswordChange = true, canEditProfile = true }: UniversalSettingsProps & { clients: Clients }) {
  const providers = useCentralResource(
    useCallback(() => clients.settings.getAuthProviders(), [clients]),
    { onUnauthenticated: () => onUnauthenticated() },
  );
  const saveProfile = useCallback(
    async (draft: ProfileDraft) => {
      await settle(await clients.settings.updateProfile(draft), onUnauthenticated);
      onChanged();
    },
    [clients, onUnauthenticated, onChanged],
  );
  const uploadPhoto = useCallback(
    async (file: File) => {
      await settle(
        await uploadCentralImageAsset({ assets: clients.assets, assetType: 'user-avatar', entityId: user.id, file }),
        onUnauthenticated,
      );
      onChanged();
    },
    [clients, user.id, onUnauthenticated, onChanged],
  );
  const hasPassword = providers.status === 'ready' && providers.data ? providers.data.password.linked : null;
  return (
    <ProfileSection
      avatarUrl={user.avatarUrl ?? null}
      fullName={user.name ?? null}
      email={user.email}
      onSaveProfile={canEditProfile ? saveProfile : undefined}
      onUploadAvatarFile={canEditProfile ? uploadPhoto : undefined}
      onSignOut={onSignOut}
    >
      <AccountSecuritySection
        email={user.email}
        hasPassword={hasPassword}
        usesGoogle={providers.status === 'ready' ? providers.data?.google.linked === true : undefined}
        passwordResetUrl={buildCentralPasswordResetUrl(centralUrl)}
        onUnauthenticated={onUnauthenticated}
        onChangePassword={
          allowPasswordChange
            ? async (input) => {
                const r = unwrapCentralResult(await clients.settings.changePassword(input));
                const ended = (r as { otherSessionsEnded?: number }).otherSessionsEnded;
                return typeof ended === 'number' ? { otherSessionsEnded: ended } : {};
              }
            : undefined
        }
      />
    </ProfileSection>
  );
}

// ── Workspace (+ D4 lifecycle hand-offs) ─────────────────────────────────────

function WorkspaceTab({ workspace, clients, centralUrl, returnTo, onUnauthenticated, onChanged }: UniversalSettingsProps & { clients: Clients }) {
  const canEdit = MANAGER_ROLES.has(roleOf(workspace)); // Central: owner/admin by role
  const purpose = purposeOf(workspace);
  const saveWorkspace = useCallback(
    async (draft: WorkspaceDraft) => {
      await settle(await clients.settings.updateWorkspace(workspace.id, draft), onUnauthenticated);
      onChanged();
    },
    [clients, workspace.id, onUnauthenticated, onChanged],
  );
  const uploadLogo = useCallback(
    async (file: File) => {
      await settle(
        await uploadCentralImageAsset({ assets: clients.assets, assetType: 'workspace-avatar', entityId: workspace.id, file }),
        onUnauthenticated,
      );
      onChanged();
    },
    [clients, workspace.id, onUnauthenticated, onChanged],
  );
  return (
    <WorkspaceSection
      name={workspace.name}
      slug={workspace.slug ?? ''}
      type={purpose ?? (workspace.type === 'personal' ? 'personal' : 'business')}
      canEditPurpose={roleOf(workspace) === 'owner' && purpose !== null}
      role={workspace.role}
      id={workspace.id}
      avatarUrl={workspace.avatarUrl ?? null}
      status={workspace.status ?? undefined}
      createdAt={workspace.createdAt ?? null}
      onboardingVersion={workspace.onboardingVersion ?? null}
      onSaveWorkspace={canEdit ? saveWorkspace : undefined}
      onUploadAvatarFile={canEdit ? uploadLogo : undefined}
      lifecycle={{
        centralUrl,
        workspaceId: workspace.id,
        returnTo: returnTo('workspace'),
        canTransferOwnership: permission(workspace, 'transferOwnership'),
        canCloseWorkspace: permission(workspace, 'closeWorkspace'),
      }}
    />
  );
}

// ── Organization (tree + manager) ────────────────────────────────────────────

function OrganizationTab({ workspace, clients, handlers, onSelectWorkspace }: TabProps) {
  const graph = useCentralResource(useCallback(() => clients.settings.getOrganizationGraph(), [clients]), handlers);
  return (
    <>
      <OrganizationSection
        graph={graph.data}
        isLoading={graph.status === 'loading'}
        error={graph.status === 'error' || graph.status === 'forbidden' ? graph.error : null}
        onRetry={() => void graph.reload()}
        currentWorkspaceId={workspace.id}
        onSelectWorkspace={onSelectWorkspace ? (node) => node.operatingWorkspaceId && onSelectWorkspace(node.operatingWorkspaceId) : undefined}
      />
      {graph.status === 'ready' && graph.data && (
        <OrganizationManager
          graph={graph.data}
          organization={clients.organization}
          onChanged={() => graph.reload()}
          currentWorkspaceId={workspace.id}
          {...handlers}
        />
      )}
    </>
  );
}

// ── Members (people · teams · roles) + per-app access (D1) ──────────────────

function MembersTab(props: TabProps) {
  const { workspace, user, clients, handlers, view, onNavigate, appId, apps, onChanged } = props;
  const isManager = MANAGER_ROLES.has(roleOf(workspace));
  const permissions = useMemo(
    () =>
      buildMembershipPermissions({
        currentWorkspace: {
          id: workspace.id,
          name: workspace.name,
          role: roleOf(workspace),
          permissions: workspace.permissions ?? undefined,
        } as never,
        apps: [],
        meta: workspace.permissions ? ({ features: { workspacePermissions: true } } as never) : null,
      }),
    [workspace],
  );

  const members = useCentralResource(useCallback(() => clients.settings.listMembers(workspace.id), [clients, workspace.id]), handlers);
  const invites = useCentralResource(
    useCallback(
      () => (isManager ? clients.settings.listInvites(workspace.id) : Promise.resolve({ ok: true, status: 200, data: [] } as CentralResult<never[]>)),
      [clients, workspace.id, isManager],
    ),
    handlers,
  );
  // What the caller may grant per app (owner/admin only; Central bounds it).
  const assignable = useCentralResource(
    useCallback(
      () => (isManager ? clients.settings.getAssignableApps(workspace.id) : Promise.resolve({ ok: true, status: 200, data: null } as CentralResult<null>)),
      [clients, workspace.id, isManager],
    ),
    handlers,
  );

  const rows = useMemo(
    () => ((members.data ?? []) as unknown as Record<string, unknown>[]).map(toWorkspaceMember),
    [members.data],
  );
  const pending = useMemo(
    () => ((invites.data ?? []) as unknown as Record<string, unknown>[]).map(toPendingInvite),
    [invites.data],
  );
  const appNames = useMemo(
    () => Object.fromEntries((apps ?? []).map((a) => [a.appId, a.displayName ?? a.name ?? a.appId])),
    [apps],
  );
  const [openMemberId, setOpenMemberId] = useState<string | null>(null);
  const openMember = rows.find((m) => m.userId === openMemberId) ?? null;
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [inviting, setInviting] = useState(false);

  const after = useCallback(async () => {
    await Promise.all([members.reload(), invites.reload()]);
    onChanged();
  }, [members, invites, onChanged]);

  const onInvite = useCallback(
    async (p: InvitePayload) => {
      setInviting(true);
      setInviteError(null);
      try {
        await settle(
          await clients.settings.inviteMember(workspace.id, {
            email: p.email,
            role: (p.workspaceRole ?? p.role) as 'admin' | 'member' | 'viewer',
            ...(p.templateId ? { templateId: p.templateId } : {}),
            ...(p.appRoles && Object.keys(p.appRoles).length ? { appRoles: p.appRoles as Record<string, 'admin' | 'member' | 'viewer'> } : {}),
            originatingAppId: appId,
          }),
          handlers.onUnauthenticated,
        );
        await invites.reload();
      } catch (err) {
        setInviteError(err instanceof Error ? err.message : 'The invitation was not sent.');
        throw err;
      } finally {
        setInviting(false);
      }
    },
    [clients, workspace.id, appId, handlers, invites],
  );
  const onUpdateRole = useCallback(
    async (userId: string, role: string) => {
      await settle(await clients.settings.updateMemberRole(workspace.id, userId, role as 'admin' | 'member' | 'viewer'), handlers.onUnauthenticated);
      await after();
    },
    [clients, workspace.id, handlers, after],
  );
  const onUpdateMemberAppRoles = useCallback(
    async (userId: string, appRoles: Record<string, string>) => {
      await settle(
        await clients.settings.updateMemberAppRoles(workspace.id, userId, appRoles as Record<string, 'admin' | 'member' | 'viewer'>),
        handlers.onUnauthenticated,
      );
      await after();
    },
    [clients, workspace.id, handlers, after],
  );
  const onRemoveMember = useCallback(
    async (userId: string) => {
      await settle(await clients.settings.removeMember(workspace.id, userId), handlers.onUnauthenticated);
      setOpenMemberId(null);
      await after();
    },
    [clients, workspace.id, handlers, after],
  );

  const subnav = (
    <nav aria-label="Members views" style={{ display: 'flex', gap: 8, marginBottom: 16 }} data-testid="members-views">
      {(['people', 'teams', 'roles'] as const).map((v) => (
        <button
          key={v}
          type="button"
          data-testid={`members-view-${v}`}
          aria-current={(view ?? 'people') === v ? 'page' : undefined}
          onClick={() => onNavigate('members', v === 'people' ? null : v)}
          style={{ fontWeight: (view ?? 'people') === v ? 600 : 400, background: 'none', border: 'none', cursor: 'pointer', padding: '4px 8px' }}
        >
          {v === 'people' ? 'People' : v === 'teams' ? 'Teams' : 'Roles'}
        </button>
      ))}
    </nav>
  );

  if (view === 'teams') {
    const people = rows.map((m) => ({ userId: m.userId, name: m.fullName, email: m.email }));
    return (
      <>
        {subnav}
        <TeamsSection teams={clients.teams} people={people} canManage={roleOf(workspace) === 'owner'} {...handlers} />
      </>
    );
  }
  if (view === 'roles') {
    return (
      <>
        {subnav}
        <RolesPermissionsSection
          workspaceType={workspace.type ?? null}
          members={rows.map((m) => ({ userId: m.userId, name: m.fullName, email: m.email, role: m.role, ...(m.appRoles ? { appRoles: m.appRoles } : {}) }))}
          currentRole={(roleOf(workspace) || null) as never}
          currentUserId={user.id}
          appNames={appNames}
          onEditMember={isManager ? (m) => setOpenMemberId(m.userId) : undefined}
        />
        {openMember && (
          <MemberAccessPanel
            workspaceType={workspace.type ?? null}
            member={openMember as never}
            permissions={permissions}
            currentUserId={user.id}
            assignableApps={(assignable.data as never) ?? null}
            appNames={appNames}
            onUpdateRole={onUpdateRole}
            onUpdateMemberAppRoles={onUpdateMemberAppRoles}
            onRemoveMember={onRemoveMember}
            onClose={() => setOpenMemberId(null)}
          />
        )}
      </>
    );
  }
  return (
    <>
      {subnav}
      <MembershipSection
        workspaceType={workspace.type ?? null}
        members={rows as never}
        invites={pending as never}
        permissions={permissions}
        currentUserId={user.id}
        isLoadingMembers={members.status === 'loading'}
        isLoadingInvites={invites.status === 'loading'}
        membersError={members.status === 'error' || members.status === 'forbidden' ? members.error : null}
        invitesError={invites.status === 'error' ? invites.error : null}
        onInvite={onInvite}
        originatingAppId={appId}
        onUpdateRole={onUpdateRole}
        onRemoveMember={onRemoveMember}
        onResendInvite={async (inviteId) => {
          await settle(await clients.settings.resendInvite(workspace.id, inviteId), handlers.onUnauthenticated);
        }}
        onCancelInvite={async (inviteId) => {
          await settle(await clients.settings.revokeInvite(workspace.id, inviteId), handlers.onUnauthenticated);
          await invites.reload();
        }}
        assignableApps={(assignable.data as never) ?? null}
        isLoadingAssignableApps={assignable.status === 'loading'}
        onUpdateMemberAppRoles={isManager ? onUpdateMemberAppRoles : undefined}
        showAppAccessSummary={isManager}
        onOpenMember={(m) => setOpenMemberId(m.userId)}
        isInviteSubmitting={inviting}
        inviteError={inviteError}
      />
      {openMember && (
        <MemberAccessPanel
          workspaceType={workspace.type ?? null}
          member={openMember as never}
          permissions={permissions}
          currentUserId={user.id}
          assignableApps={(assignable.data as never) ?? null}
          appNames={appNames}
          onUpdateRole={onUpdateRole}
          onUpdateMemberAppRoles={onUpdateMemberAppRoles}
          onRemoveMember={onRemoveMember}
          onClose={() => setOpenMemberId(null)}
        />
      )}
    </>
  );
}

// ── Availability ─────────────────────────────────────────────────────────────

function usePeople(clients: Clients, workspaceId: string, handlers: Handlers) {
  return useCentralResource(useCallback(() => clients.settings.listPeople(workspaceId), [clients, workspaceId]), handlers);
}

function MyAvailabilityTab({ workspace, user, clients, handlers, onUnavailable }: TabProps & { onUnavailable: () => void }) {
  const people = usePeople(clients, workspace.id, handlers);
  if (people.status === 'loading') return <p data-testid="my-availability-loading">Loading…</p>;
  const me = people.data?.members.find((m) => m.userId === user.id) ?? null;
  return (
    <MyAvailabilitySection
      availability={clients.availability}
      membershipId={me?.membershipId ?? null}
      {...handlers}
      onUnavailable={onUnavailable}
    />
  );
}

function AvailabilityTab({ workspace, clients, handlers, onUnavailable, onUnauthenticated }: TabProps & { onUnavailable: () => void }) {
  const people = usePeople(clients, workspace.id, handlers);
  const details = useCentralResource(useCallback(() => clients.settings.getWorkspace(workspace.id), [clients, workspace.id]), handlers);
  if (people.status === 'loading' || details.status === 'loading') return <p data-testid="availability-loading">Loading…</p>;
  const tz = (details.data as { defaultTimezone?: string | null; workspace?: { defaultTimezone?: string | null } } | null);
  return (
    <WorkspaceAvailabilitySection
      availability={clients.availability}
      people={(people.data?.members ?? []).map((m) => ({ membershipId: m.membershipId, name: m.name, email: m.email }))}
      workspaceTimezone={tz?.defaultTimezone ?? tz?.workspace?.defaultTimezone ?? null}
      onSaveWorkspaceTimezone={async (timezone) => {
        await settle(await clients.settings.updateWorkspaceLocalization(workspace.id, { defaultTimezone: timezone } as never), onUnauthenticated);
        await details.reload();
      }}
      {...handlers}
      onUnavailable={onUnavailable}
    />
  );
}

// ── Billing (plan picker · invoices) ─────────────────────────────────────────

function BillingTab({ workspace, clients, handlers, view, outcome, apps, centralUrl, returnTo, onNavigate, onChanged }: TabProps) {
  const appNames = useMemo(
    () => Object.fromEntries((apps ?? []).map((a) => [a.appId, a.displayName ?? a.name ?? a.appId])),
    [apps],
  );
  return (
    <BillingPanel
      client={clients.billing}
      workspaceId={workspace.id}
      workspaceType={workspace.type ?? null}
      canManage={permission(workspace, 'manageBilling')}
      centralBaseUrl={centralUrl.replace(/\/+$/, '')}
      returnTo={returnTo('billing')}
      showPlans={view === 'plans'}
      outcome={outcome ?? null}
      appNames={appNames}
      onOpenPlans={() => onNavigate('billing', 'plans')}
      onClosePlans={() => onNavigate('billing', null)}
      onChanged={onChanged}
      onUnauthenticated={handlers.onUnauthenticated}
    />
  );
}

// ── Credits (usage · packs) ──────────────────────────────────────────────────

function CreditsTab({ workspace, clients, handlers, centralUrl, returnTo }: TabProps) {
  const credits = useCentralResource(
    useCallback(() => clients.billing.getCredits(workspace.id), [clients, workspace.id]),
    handlers,
  );
  return (
    <>
      <CreditsUsageSection
        credits={credits.status === 'ready' ? (credits.data as unknown as CentralCreditsPayload) : null}
        isLoading={credits.status === 'loading'}
        error={credits.status === 'error' ? credits.error : null}
        onRetry={() => void credits.reload()}
      />
      <CreditPackPicker
        client={clients.billing}
        workspaceId={workspace.id}
        centralBaseUrl={centralUrl.replace(/\/+$/, '')}
        returnTo={returnTo('credits')}
        canPurchase={permission(workspace, 'manageBilling')}
        credits={credits.status === 'ready' ? (credits.data as never) : null}
        {...handlers}
      />
      {permission(workspace, 'manageBilling') && (
        <CreditTransactionsSection client={clients.billing} workspaceId={workspace.id} hideWhenForbidden className="mt-6" />
      )}
    </>
  );
}

// ── Apps & Access (catalog · who can open what) ──────────────────────────────

function AppsAccessTab({ workspace, clients, handlers, onNavigate, onChanged, onOpenApp, apps }: TabProps) {
  const isManager = MANAGER_ROLES.has(roleOf(workspace));
  const members = useCentralResource(
    useCallback(
      () => (isManager ? clients.settings.listMembers(workspace.id) : Promise.resolve({ ok: true, status: 200, data: [] } as CentralResult<never[]>)),
      [clients, workspace.id, isManager],
    ),
    handlers,
  );
  return (
    <>
      <CentralAppCatalog
        client={clients.billing}
        workspaceId={workspace.id}
        onOpenApp={(app) => {
          if (!app.launchUrl) return;
          if (onOpenApp) onOpenApp(app.launchUrl);
          else window.location.assign(app.launchUrl);
        }}
        canManageBilling={permission(workspace, 'manageBilling')}
        onComparePlans={(entry) => onNavigate('billing', 'plans', { outcome: entry.enableOutcome?.code ?? null })}
        onChanged={() => onChanged()}
      />
      {isManager && members.status === 'ready' && (
        <MemberAppAccessSummary
          workspaceType={workspace.type ?? null}
          members={((members.data ?? []) as unknown as Record<string, unknown>[]).map((m) => toWorkspaceMember(m)) as never}
          apps={(apps ?? []).map((a) => ({ appId: a.appId, displayName: a.displayName ?? a.name ?? a.appId }))}
          onOpenMember={() => onNavigate('members', null)}
        />
      )}
    </>
  );
}

// ── Notifications ────────────────────────────────────────────────────────────

function NotificationsTab({ workspace, clients, handlers }: TabProps) {
  const prefs = useCentralResource(
    useCallback(() => clients.settings.getNotificationPreferences(workspace.id), [clients, workspace.id]),
    handlers,
  );
  return (
    <NotificationPreferencesSection
      preferences={(prefs.data ?? []) as never}
      isLoading={prefs.status === 'loading'}
      error={prefs.status === 'error' ? prefs.error : null}
      onUpdateCategory={(category, channel, enabled) => {
        void clients.settings
          .updateNotificationPreference(category, { workspaceId: workspace.id, channel: channel as 'inbox' | 'push' | 'email' | 'sms', enabled })
          .then((r) => {
            if (!r.ok && r.status === 401) handlers.onUnauthenticated();
            else void prefs.reload();
          });
      }}
    />
  );
}
